// AI-CONTEXT: See docs/features/impact-per-euro-methodiek.md
import firebase from '../../firebase/compat'
import { db } from '../../firebase/config'

/**
 * Snapshot-log van de participatieladder (methodiek-besluit D4): elke
 * profiel-save schrijft verplicht een meting naar `ParticipationMeasurements`.
 * Het profielformulier saved per veldwijziging (autosave), dus we schrijven
 * één document per persona per dag — deterministische docid, laatste save van
 * de dag wint. De handmatige "meting vastleggen"-knop gebruikt hetzelfde pad
 * met `Source: 'manual'`.
 */

/**
 * Lokale dagsleutel (yyyy-mm-dd) — lokale tijdzone, zodat een avond-save niet
 * op de UTC-volgende dag belandt.
 *
 * @param {Date} date
 * @returns {string}
 */
const dagSleutel = (date) => {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/**
 * Eén docid per persona per dag — de upsert-sleutel van de snapshot-log.
 *
 * @param {string} personaId - Persona `ID` (niet docid)
 * @param {Date} date
 * @returns {string}
 */
const participationSnapshotDocId = (personaId, date) => `${personaId}_${dagSleutel(date)}`

/**
 * @typedef {object} SnapshotInput
 * @property {string} client - CompagnyID van de tenant
 * @property {string} personaId - Persona `ID`
 * @property {object} profile - Het t1-profiel op het meetmoment (mag onvolledig zijn — autosave per veld)
 * @property {{ t1?: { ladderStep?: number }, delta?: { totalSocietalValue?: number, governmentBalance?: number, personalIncomeChange?: number }, perStakeholder?: Object<string, number>, perType?: Object<string, number> }|null} impact - Berekend resultaat van calculateParticipationImpact, of null
 * @property {string} [parametersVersion] - Meetstandaard-parameterversie
 * @property {'auto'|'manual'} source - Automatische save of handmatige vastlegging
 * @property {Date} [date] - Meetdag (handmatige, evt. teruggezette datum; injecteerbaar voor tests). Ontbreekt bij de automatische snapshot.
 */

/**
 * Pure payload-bouwer — het document dat in `ParticipationMeasurements` landt.
 *
 * @param {SnapshotInput & { docId: string, date: Date, timestamp: * }} input - `timestamp` is de reeds bepaalde Timestamp-waarde (server-nu of een vaste Date)
 * @returns {object}
 */
const buildParticipationSnapshot = ({ docId, client, personaId, profile, impact, parametersVersion, source, date, timestamp }) => ({
  ID: docId,
  CompagnyID: client,
  Compagny: client,
  PersonaID: personaId,
  Profile: profile,
  LadderStep: impact?.t1?.ladderStep ?? null,
  TotalSocietalValue: impact?.delta?.totalSocietalValue ?? null,
  GovernmentBalance: impact?.delta?.governmentBalance ?? null,
  PersonalIncomeChange: impact?.delta?.personalIncomeChange ?? null,
  ParametersVersion: parametersVersion ?? null,
  // Parameterset 2026.3: wie wat kreeg. Alleen als de uitkomst ze heeft, zodat
  // een 2026.2-meting geen lege velden draagt die 'nul' lijken te zeggen.
  ...(impact?.perStakeholder ? { PerStakeholder: impact.perStakeholder, PerType: impact.perType } : {}),
  Source: source,
  Date: dagSleutel(date),
  Timestamp: timestamp,
})

/**
 * Schrijft (upsert) de dag-snapshot van een persona naar
 * `ParticipationMeasurements`.
 *
 * @param {SnapshotInput} input
 * @returns {Promise<void>}
 */
const recordParticipationSnapshot = ({ client, personaId, profile, impact, parametersVersion, source, date }) => {
  const meetdag = date || new Date()
  const docId = participationSnapshotDocId(personaId, meetdag)

  // Expliciete meetdag (handmatig, mogelijk terug in de tijd) → vaste Timestamp
  // op 12:00 lokaal, net als een datumcorrectie (moveParticipationSnapshot),
  // zodat de meting op de gekozen dag in de lijst en het juiste boekjaar landt.
  // De lijst leest `dagVan(Timestamp)`, dus server-nu zou een teruggezette
  // meting alsnog op vandaag tonen. Geen datum (automatische snapshot) → server-nu.
  const timestamp = date
    ? new Date(meetdag.getFullYear(), meetdag.getMonth(), meetdag.getDate(), 12, 0, 0)
    : firebase.firestore.FieldValue.serverTimestamp()

  return db.collection('ParticipationMeasurements')
    .doc(docId)
    .set(buildParticipationSnapshot({
      docId, client, personaId, profile, impact, parametersVersion, source, date: meetdag, timestamp,
    }))
}

export default recordParticipationSnapshot
export { participationSnapshotDocId, buildParticipationSnapshot }
